/**
 * The handful of glyphs the job list needs, inline.
 *
 * Inline rather than an icon package: three shapes do not justify a dependency,
 * and drawing them with `currentColor` means they follow the button's text
 * colour through the light and dark themes without a second set of assets.
 */

interface Props {
  name: 'eye' | 'markdown' | 'pdf' | 'json';
}

const COMMON = {
  width: 16,
  height: 16,
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.4,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  // Decorative: the button around it carries the name, via aria-label and title.
  'aria-hidden': true,
  focusable: false,
};

export function Icon({ name }: Props) {
  switch (name) {
    case 'eye':
      return (
        <svg {...COMMON} className="icon">
          <path d="M1.4 8s2.5-4.4 6.6-4.4S14.6 8 14.6 8s-2.5 4.4-6.6 4.4S1.4 8 1.4 8Z" />
          <circle cx="8" cy="8" r="2.1" />
        </svg>
      );

    case 'markdown':
      // The Markdown mark itself: a rounded frame around an "M" and a down arrow.
      return (
        <svg {...COMMON} className="icon">
          <rect x="0.9" y="3.1" width="14.2" height="9.8" rx="1.8" />
          <path d="M3.6 10.6V5.9l2 2.3 2-2.3v4.7" />
          <path d="M11.1 5.9v4.2M9.5 8.7l1.6 1.7 1.6-1.7" />
        </svg>
      );

    case 'pdf':
      // A sheet with a folded corner and a filled badge sitting *inside* it.
      // The badge is what distinguishes it from a plain document next to the
      // Markdown mark; spanning the full width instead turns the whole glyph
      // into something that reads as a printer.
      return (
        <svg {...COMMON} className="icon">
          <path d="M4 1.3h4.6l4 4v9a1.3 1.3 0 0 1-1.3 1.3H4a1.3 1.3 0 0 1-1.3-1.3V2.6A1.3 1.3 0 0 1 4 1.3Z" />
          <path d="M8.5 1.4v3.9h3.9" />
          <rect x="4.7" y="10.2" width="5.4" height="2.7" rx="0.7" fill="currentColor" stroke="none" />
        </svg>
      );

    case 'json':
      // Braces: the one shape nobody mistakes for prose.
      return (
        <svg {...COMMON} className="icon">
          <path d="M6.3 2.4c-1.6 0-1.9.9-1.9 2.1v1.3c0 1.2-.6 1.7-1.5 1.7v1c.9 0 1.5.5 1.5 1.7v1.3c0 1.2.3 2.1 1.9 2.1" />
          <path d="M9.7 2.4c1.6 0 1.9.9 1.9 2.1v1.3c0 1.2.6 1.7 1.5 1.7v1c-.9 0-1.5.5-1.5 1.7v1.3c0 1.2-.3 2.1-1.9 2.1" />
        </svg>
      );
  }
}
